import { useMemo } from "react";
import { DISPLAY_DATE_WITH_TIME } from "@constants/date-formats";
import { resources } from "@constants/hardcoded-strings";
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from "@constants/table-defaults";
import { FormattedVerificationTransactionDetailsTransactionEvent } from "@idw-types/data/verification-transaction-details";
import { formatDateToString, sortDates } from "@utils/date-util";
import { sortStrings } from "@utils/string-util";
import { IdwTable } from "../../../common/idw-table/idw-table";
import { PaginationContainer } from "../../../common/pagination-container/pagination-container";
import { IdwTableContainer } from "../../../common/idw-table-container/idw-table-container";
import styles from "./transaction-events-table-container.less";
import { createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";

type TransactionEventsTableContainerProps = {
    transactionEvents: Array<FormattedVerificationTransactionDetailsTransactionEvent>;
};

type TransactionEventsTableDataRow = FormattedVerificationTransactionDetailsTransactionEvent & {
    transactionTypeDescription: string;
};

export const TransactionEventsTableContainer = (props: TransactionEventsTableContainerProps) => {
    const { transactionEvents } = props;

    const transactionEventTableData = useMemo(() => {
        return transactionEvents.map(transactionEvent => ({
            ...transactionEvent,
            transactionTypeDescription: transactionEvent.transactionType?.description || ""
        }))
    }, [transactionEvents]);

    const columnHelper = createColumnHelper<TransactionEventsTableDataRow>();
    const columns = useMemo(() => ([
        columnHelper.accessor("date", {
            header: resources.tables.verificationTransactionDetailsTransactionEvents.columns.date.header,
            cell: (props) => props.getValue() ? <span>{formatDateToString(props.getValue(), DISPLAY_DATE_WITH_TIME)}</span> : null,
            sortingFn: (a, b) => sortDates(a.original.date, b.original.date)
        }),
        columnHelper.accessor("rpServiceName", {
            header: resources.tables.verificationTransactionDetailsTransactionEvents.columns.verificationService.header,
            sortingFn: (a, b) => sortStrings(a.original.rpServiceName, b.original.rpServiceName)
        }),
        columnHelper.accessor("apiKey", {
            header: resources.tables.verificationTransactionDetailsTransactionEvents.columns.apiKey.header
        }),
        columnHelper.accessor("transactionTypeDescription", {
            header: resources.tables.verificationTransactionDetailsTransactionEvents.columns.transactionEventDescription.header
        }),
        columnHelper.accessor("details", {
            header: resources.tables.verificationTransactionDetailsTransactionEvents.columns.details.header,
            cell: (props) => {
                const details = props.row.original.details;
                if (Object.keys(details).length > 0) {
                    return (
                        <div>
                            {Object.keys(details).map((key, index) => (
                                <div key={`${key}-${index}`}><b>{key}</b>: {details[key]}</div>
                            ))}
                        </div>
                    );
                } else {
                    return null;
                }
            }
        })
    ]), [transactionEventTableData]);

    const useTableResult = useReactTable({
        columns,
        data: transactionEventTableData,
        filterFns: null,
        sortDescFirst: false,
        enableFilters: false,
        initialState: {
            pagination: {
                pageIndex: DEFAULT_PAGE_INDEX,
                pageSize: DEFAULT_PAGE_SIZE
            }
        },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer>
            <IdwTable
                id="transaction-events"
                className={styles.transactionEventsTable}
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"transaction-events"}
            />
            <PaginationContainer
                idPrefix="transaction-events"
                useTableResult={useTableResult}
            />
        </IdwTableContainer>
    );
};